import { getAttributeValue } from '../../utils/variants';
import {
  DELIVERY_METHODS_SMALL_PRODUCT,
  DELIVERY_METHODS_LARGE_PRODUCT,
} from '../../blocks/Checkout/DeliveryMethodsStep/constants';
import {
  IN_HOME_SETUP_PAID_SHIPPING_METHOD_KEY,
  STANDARD_SHIPPING_KEY,
} from '../../utils/common/constants';

export const isValidCartShippingMethods = ({ lineItems, customLineItems }) => {
  const lineItemsIds = lineItems.map((lineItem) => lineItem.id);
  const shippingMethodsIds = customLineItems
    .flatMap((customLineItem) => customLineItem.custom?.fields.lineItemsIds)
    .filter(Boolean);

  return (
    lineItemsIds.every((id) => shippingMethodsIds.includes(id)) &&
    shippingMethodsIds.every((id) => lineItemsIds.includes(id))
  );
};

const getSelectedShippingMethods = (cart, skus, locale) =>
  !cart
    ? {}
    : skus.reduce((acc, sku) => {
        const lineItem = cart.lineItems.find((li) => li.variant.sku === sku);
        const shippingMethodCustomField = cart.customLineItems.find(
          (customLineItem) =>
            customLineItem.custom?.fields?.netsuite_shipping_name &&
            customLineItem.custom?.fields?.lineItemsIds?.includes(lineItem.id),
        );
        return {
          key: shippingMethodCustomField?.custom.fields.netsuite_shipping_name,
          needsRemoval:
            shippingMethodCustomField?.custom.fields.mattress_removal,
          cost: shippingMethodCustomField?.money.centAmount,
          name: shippingMethodCustomField?.name[locale],
        };
      }, {});

export const getSelectedShippingMethodsForAvailables = ({
  availableShippingMethods,
  cart,
}) =>
  availableShippingMethods?.map((availableShippingMethod) => ({
    ...availableShippingMethod,
    groups: availableShippingMethod.groups?.map((group) => {
      const relatedLineItems = cart?.lineItems?.filter((currentLineItem) =>
        group.products?.find(
          (productForGroup) =>
            productForGroup.sku === currentLineItem?.variant?.sku,
        ),
      );
      const relatedCustomLineItem = cart?.customLineItems?.find(
        (currentCustomLineItem) =>
          relatedLineItems?.length > 0
            ? currentCustomLineItem.custom?.fields.netsuite_shipping_name &&
              relatedLineItems?.every((relatedLineItem) =>
                currentCustomLineItem.custom?.fields?.lineItemsIds?.find(
                  (currentLineItemId) =>
                    currentLineItemId === relatedLineItem.id,
                ),
              )
            : undefined,
      );
      return {
        ...group,
        selectedShippingMethod: relatedCustomLineItem
          ? {
              ...group.shippingMethods?.find(
                (currentShippingMethod) =>
                  currentShippingMethod.key ===
                  relatedCustomLineItem.custom?.fields.netsuite_shipping_name,
              ),
              price: relatedCustomLineItem.money,
              needsRemoval:
                typeof relatedCustomLineItem.custom?.fields.mattress_removal ===
                  'boolean' &&
                relatedCustomLineItem.custom?.fields.mattress_removal === true
                  ? relatedCustomLineItem.custom?.fields.mattress_removal
                  : false,
            }
          : undefined,
      };
    }),
  }));

export const getCartLineItemsGroupedBySmallAndLarge = ({ lineItems }) => {
  const largeProductsProductTypes = [
    'mattress-type',
    'base-type',
    'foundation-type',
  ];

  const cartLineItemsGroupedBySmallAndLarge = lineItems.reduce(
    (acc, lineItem) => {
      if (largeProductsProductTypes.includes(lineItem.productType?.obj?.key)) {
        return {
          ...acc,
          large: [...acc.large, lineItem],
        };
      }
      return {
        ...acc,
        small: [...acc.small, lineItem],
      };
    },
    { small: [], large: [] },
  );

  return cartLineItemsGroupedBySmallAndLarge;
};

export const getGroupedSelectedShippingMethodsWithProducts = ({
  cart,
  locale,
}) => {
  const { lineItems } = cart;

  const cartLineItemsGroupedBySmallAndLarge =
    getCartLineItemsGroupedBySmallAndLarge({ lineItems });

  return {
    shippingMethods:
      cart?.customLineItems
        ?.filter(
          (customLineItem) =>
            customLineItem?.custom?.fields?.netsuite_shipping_name &&
            customLineItem?.custom?.fields?.lineItemsIds.length > 0,
        )
        .reduce((acc, customLineItem) => {
          const shippingMethodForSmallProducts = {
            key: customLineItem.custom?.fields?.netsuite_shipping_name,
            name: customLineItem.name?.[locale],
            needsRemoval: customLineItem.custom?.fields?.mattress_removal,
            products: cartLineItemsGroupedBySmallAndLarge.small
              ?.filter((lineItem) =>
                customLineItem.custom?.fields?.lineItemsIds.includes(
                  lineItem.id,
                ),
              )
              ?.map((currentLineItem) => ({
                name: currentLineItem.name?.[locale],
                productId: currentLineItem.productId,
                productKey: currentLineItem.productKey,
                quantity: currentLineItem.quantity,
                variant: currentLineItem.variant,
              })),

            size: DELIVERY_METHODS_SMALL_PRODUCT,
          };

          const shippingMethodForLargeProducts = {
            key: customLineItem.custom?.fields?.netsuite_shipping_name,
            name: customLineItem.name?.[locale],
            needsRemoval: customLineItem.custom?.fields?.mattress_removal,
            products: cartLineItemsGroupedBySmallAndLarge.large
              ?.filter((lineItem) =>
                customLineItem.custom?.fields?.lineItemsIds.includes(
                  lineItem.id,
                ),
              )
              ?.map((currentLineItem) => ({
                name: currentLineItem.name?.[locale],
                productId: currentLineItem.productId,
                productKey: currentLineItem.productKey,
                quantity: currentLineItem.quantity,
                variant: currentLineItem.variant,
              })),
            size: DELIVERY_METHODS_LARGE_PRODUCT,
          };

          const newShippingMethods = [
            shippingMethodForSmallProducts,
            shippingMethodForLargeProducts,
          ].filter(
            (currentShippingMethod) =>
              !!currentShippingMethod.products?.length > 0,
          );
          return [...acc, ...newShippingMethods.flat()];
        }, []) ?? [],
  };
};

// Helper function to get the existing shipping method for a group of products from cart
const getExistingShippingMethodForGroup = (products, cart) => {
  if (!cart || !cart.customLineItems || !cart.lineItems) {
    return null;
  }

  // Get line item IDs for all products in the group
  const lineItemIds = products
    .map((product) => {
      const lineItem = cart.lineItems.find((li) => li.variant?.sku === product.sku);
      return lineItem?.id;
    })
    .filter(Boolean);

  if (lineItemIds.length === 0) {
    return null;
  }

  // Find a custom line item that covers exactly these line items
  const matchingCustomLineItem = cart.customLineItems.find((customLineItem) => {
    const customLineItemIds = customLineItem.custom?.fields?.lineItemsIds || [];
    const shippingMethodKey = customLineItem.custom?.fields?.netsuite_shipping_name;

    // Check if this custom line item covers exactly the products in this group
    return (
      shippingMethodKey &&
      lineItemIds.length === customLineItemIds.length &&
      lineItemIds.every((id) => customLineItemIds.includes(id))
    );
  });

  if (matchingCustomLineItem) {
    return {
      key: matchingCustomLineItem.custom?.fields?.netsuite_shipping_name,
      needsRemoval: matchingCustomLineItem.custom?.fields?.mattress_removal || false,
      cost: matchingCustomLineItem.money?.centAmount,
    };
  }

  return null;
};

export const getFreeOrMinorPriceShippingMethodFromAvailable = ({
  availableShippingMethod,
  cart,
}) => ({
  parentKey: availableShippingMethod.key,
  groups: availableShippingMethod.groups.map(
    ({ products, shippingMethods }) => {
      const skus = products.map(({ sku }) => sku);
      
      // Check if there's an existing shipping method selection for this group in the cart
      const existingSelection = getExistingShippingMethodForGroup(products, cart);
      
      if (existingSelection) {
        // Find the matching shipping method from available options
        const matchingMethod = shippingMethods.find(
          (method) => method.key === existingSelection.key
        );
        
        if (matchingMethod) {
          return {
            key: matchingMethod.key,
            name: matchingMethod.name,
            cost: matchingMethod.price?.centAmount,
            needsRemoval: existingSelection.needsRemoval,
            skus,
          };
        }
      }
      
      // If no existing selection or method not found, select the cheapest option (first one after sorting)
      const { key, offerRemovalOption, cost, name } = shippingMethods.reduce(
        (acc, curr) => {
          if (
            acc.price?.centAmount === undefined ||
            curr.price?.centAmount < acc.price?.centAmount
          ) {
            return { ...curr, cost: curr.price?.centAmount };
          }
          return acc;
        },
        { price: { centAmount: undefined } },
      );

      return {
        key,
        name,
        cost,
        needsRemoval:
          typeof offerRemovalOption === 'boolean' && !!offerRemovalOption,
        skus,
      };
    },
  ),
});

export const mapAvailableShippingMethodToShippingMethod = (
  availableShippingMethod,
  cart,
  locale,
) => ({
  parentKey: availableShippingMethod.key,
  groups: availableShippingMethod.groups.map(
    ({ products, shippingMethods }) => {
      const skus = products.map(({ sku }) => sku);
      const { key, needsRemoval, cost, name } = getSelectedShippingMethods(
        cart,
        skus,
        locale,
      );

      return {
        key: key || shippingMethods[0].key,
        name: name || shippingMethods[0].name,
        cost: cost || shippingMethods[0].price.centAmount,
        needsRemoval: typeof needsRemoval === 'boolean' ? needsRemoval : false,
        skus,
      };
    },
  ),
});

export const mapResponseToAvailableShippingMethod = (
  { key, name, groups },
  lineItems,
  locale,
) => {
  const availableShippingMethods = {
    key,
    name,
    groups: groups.map(({ products, shippingMethods }, i) => ({
      id: i,
      products: lineItems
        .filter(({ variant }) => products.includes(variant.sku))
        .map(({ name: n, variant }) => ({
          name: n[locale],
          title: getAttributeValue(variant.attributes, 'title'),
          image: {
            url: variant.images[0]?.url,
            label: variant.images[0]?.label || n[locale],
          },
          sku: variant.sku,
        })),
      shippingMethods: shippingMethods.sort(
        (a, b) => a.price.centAmount - b.price.centAmount,
      ),
    })),
  };

  const cartLineItemsGroupedBySmallAndLarge =
    getCartLineItemsGroupedBySmallAndLarge({ lineItems });

  const availableShippingMethodsSplittedBySmallAndLargeProducts = {
    ...availableShippingMethods,
    groups: availableShippingMethods.groups
      .reduce((acc, currentGroup) => {
        const availableShippingMethodGroupForSmallProducts = {
          ...currentGroup,
          products: currentGroup.products.filter((product) =>
            cartLineItemsGroupedBySmallAndLarge.small.find(
              (lineItem) => lineItem.variant?.sku === product.sku,
            ),
          ),
          size: DELIVERY_METHODS_SMALL_PRODUCT,
        };

        const availableShippingMethodGroupForLargeProducts = {
          ...currentGroup,
          products: currentGroup.products.filter((product) =>
            cartLineItemsGroupedBySmallAndLarge.large.find(
              (lineItem) => lineItem.variant?.sku === product.sku,
            ),
          ),
          size: DELIVERY_METHODS_LARGE_PRODUCT,
        };

        const newAvailableShippingMethodGroups = [
          availableShippingMethodGroupForSmallProducts,
          availableShippingMethodGroupForLargeProducts,
        ].filter(
          (currentAvailableShippingMethodGroup) =>
            !!currentAvailableShippingMethodGroup.products?.length > 0,
        );
        return [...acc, ...newAvailableShippingMethodGroups.flat()];
      }, [])
      .map((currentAvailableShippingMethodGroup, index) => ({
        ...currentAvailableShippingMethodGroup,
        id: index,
      })),
  };

  return availableShippingMethodsSplittedBySmallAndLargeProducts;
};

export const changeValueShippingMethods = (
  shippingMethods,
  sm,
  parentKey,
  groupId,
) =>
  shippingMethods.map((shippingMethod) =>
    shippingMethod.parentKey === parentKey
      ? {
          parentKey,
          groups: shippingMethod.groups.map((group, i) =>
            i === groupId
              ? {
                  ...sm,
                  needsRemoval: sm.needsRemoval || false,
                  skus: group.skus,
                }
              : group,
          ),
        }
      : shippingMethod,
  );

// TODO: Remove JSDoc comments when migrating to TypeScript
/**
 * @param {Array<{parentKey: string, groups: Array<{key: string, name: string, cost: number, needsRemoval: boolean, skus: string[]}>}>} shippingMethods
 * @param {boolean} [isDraft=false]
 * @param {string | null} [storeKey=null]
 * @returns {{shippingMethods: Array<{key: string, name: string, cost: number, needsRemoval: boolean, skus: string[], storeKey?: string}>}}
 */
export const parseShippingMethodsToPayload = (
  shippingMethods, 
  isDraft = false, 
  storeKey = null
) => {
  let payloadShippingMethods = [];

  shippingMethods.forEach(({ groups }) =>
    groups.forEach((group) => {
      const foundShippingMethod = payloadShippingMethods.find(
        ({ key }) => key === group.key,
      );
      if (!foundShippingMethod) payloadShippingMethods.push(group);
      else {
        payloadShippingMethods = payloadShippingMethods.map(
          (payloadShippingMethod) =>
            payloadShippingMethod.key === group.key
              ? {
                  ...payloadShippingMethod,
                  skus: [...payloadShippingMethod.skus, ...group.skus],
                  needsRemoval: group.needsRemoval
                }
              : payloadShippingMethod,
        );
      }
    }),
  );
  // For draft carts, we need to send the store key to identify the correct shipping methods in the backend, as they are store specific
  return isDraft && storeKey ? {
    shippingMethods: payloadShippingMethods.map((payloadShippingMethod) => ({
      ...payloadShippingMethod,
      storeKey,
    })),
  } : {
    shippingMethods: payloadShippingMethods,
  };
};

// Helper function to get the custom line item for a product
const getCustomLineItemForProduct = (product, cart) => {
  const lineItem = cart.lineItems.find(
    (li) => li.variant?.sku === product.sku,
  );
  if (!lineItem) return null;

  const customLineItem = cart.customLineItems?.find((cartCustomLineItem) => {
    const lineItemIds = cartCustomLineItem.custom?.fields?.lineItemsIds || [];
    const shippingMethodKey =
      cartCustomLineItem.custom?.fields?.netsuite_shipping_name;

    return (
      shippingMethodKey &&
      lineItemIds.includes(lineItem.id)
    );
  });

  return customLineItem;
};

// Function to separate individual (Carry Out/Pick Up) from regular grouped shipping methods
export const separateIndividualAndGroupedShippingMethods = (shippingMethod, cart) => {
  const individualGroups = [];
  const regularGroups = [];

  shippingMethod.groups.forEach((group) => {
    // Map each product to its custom line item
    const productsByCustomLineItem = new Map();
    const productsWithoutCustomLineItem = [];

    group.products.forEach((product) => {
      const customLineItem = getCustomLineItemForProduct(product, cart);
      
      if (customLineItem) {
        const customLineItemId = customLineItem.id;
        if (!productsByCustomLineItem.has(customLineItemId)) {
          productsByCustomLineItem.set(customLineItemId, {
            customLineItem,
            products: [],
          });
        }
        productsByCustomLineItem.get(customLineItemId).products.push(product);
      } else {
        productsWithoutCustomLineItem.push(product);
      }
    });

    // Create individual groups for each custom line item grouping
    productsByCustomLineItem.forEach(({ customLineItem, products }) => {
      const shippingMethodKey = customLineItem.custom?.fields?.netsuite_shipping_name;
      
      // Check if in-home setup paid is available in this group
      const hasInHomeSetupPaid = group.shippingMethods.some(
        (method) => method.key === IN_HOME_SETUP_PAID_SHIPPING_METHOD_KEY,
      );
      
      // Determine which shipping methods to show
      let filteredShippingMethods;
      
      if (
        shippingMethodKey === IN_HOME_SETUP_PAID_SHIPPING_METHOD_KEY ||
        (shippingMethodKey === STANDARD_SHIPPING_KEY && hasInHomeSetupPaid)
      ) {
        // Show both in-home setup paid and standard shipping for:
        // 1. In-Home Setup Paid (allow downgrade to standard)
        // 2. Standard Shipping where in-home setup is available (allow upgrade to in-home)
        filteredShippingMethods = group.shippingMethods.filter(
          (method) => 
            method.key === IN_HOME_SETUP_PAID_SHIPPING_METHOD_KEY ||
            method.key === STANDARD_SHIPPING_KEY,
        );
      } else {
        // For all other methods (pickup, carryout, standard without in-home option),
        // show only the assigned method
        filteredShippingMethods = group.shippingMethods.filter(
          (method) => method.key === shippingMethodKey,
        );
      }

      if (filteredShippingMethods.length > 0) {
        individualGroups.push({
          ...group,
          products,
          shippingMethods: filteredShippingMethods,
          isIndividual: true,
          customLineItemId: customLineItem.id,
          selectedShippingMethodKey: shippingMethodKey,
        });
      } else {
        // If the assigned shipping method isn't available, include all methods
        individualGroups.push({
          ...group,
          products,
          shippingMethods: group.shippingMethods,
          isIndividual: true,
          customLineItemId: customLineItem.id,
          selectedShippingMethodKey: shippingMethodKey,
        });
      }
    });

    // Create regular group with remaining products (those without custom line item assignments)
    if (productsWithoutCustomLineItem.length > 0) {
      regularGroups.push({
        ...group,
        products: productsWithoutCustomLineItem,
        shippingMethods: group.shippingMethods,
        isIndividual: false,
      });
    }
  });

  return { individualGroups, regularGroups };
};
